import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ArrowDropDown, Clear, LockOutlined, OpenInNew } from '@mui/icons-material';
import type { ColumnMetadata, LookupDataItem } from '../grid';
import type { DetailFieldApi, RichTextOptions, ResponsiveWidth } from './types';
import { RichTextEditor } from './RichTextEditor';
import { LookupGridPanel } from '../grid/components/data-display/LookupGridPanel';
import { DetailFormFieldShell } from './DetailFormFieldShell';
import {
  isBooleanValueTrue,
  toBooleanStorageValue,
  formatDateTimeForInput,
  parseRelatedColumns,
  toBackendDateString,
  toBackendDateTimeString,
} from '../grid/utils';
import { isRichTextFieldMetadata, isFileFieldMetadata } from './detailFormFieldUtils';
import { DetailFormFileField } from './DetailFormFileField';
import { CustomDatePicker } from '../../components/common/CustomDatePicker';
import { isActiveEmployeeLookupItem } from './employeeActivityUtils';
import { getDetailFormJsMethodDefaultLookupId } from '../js-methods/detailFormLookupDefaults';

type DetailFormFieldProps = {
  fieldName: string;
  metadata: ColumnMetadata;
  field: DetailFieldApi;
  lookupMap?: Record<string, string>;
  lookupOptionMap?: Record<string, string>;
  lookupDataArray?: LookupDataItem[];
  defaultLookupId?: string;
  disabled?: boolean;
  isRequired: boolean;
  richTextOptions?: RichTextOptions;
  /** True while lookup data is being fetched. Shows a spinner placeholder instead of 'Loading...' when no data is available yet. */
  isLoading?: boolean;
  hideCaption?: boolean;
  captionsWidth?: ResponsiveWidth;
};

const hasLookupValue = (value: unknown): boolean =>
  value != null && value !== '' && String(value) !== '0';

export const DetailFormField = ({
  fieldName,
  metadata,
  field,
  lookupMap,
  lookupOptionMap,
  lookupDataArray,
  defaultLookupId,
  disabled = false,
  isRequired,
  richTextOptions,
  isLoading = false,
  hideCaption = false,
  captionsWidth,
}: DetailFormFieldProps) => {
  'use memo';

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const suppressOpenRef = useRef(false);

  const fieldType = useMemo<'date' | 'datetime' | 'other'>(() => {
    if (metadata?.type === 'datetime' || metadata?.dataType === 'datetime') {
      return 'datetime';
    } else if (metadata?.type === 'date' || metadata?.dataType === 'date') {
      return 'date';
    } else if (metadata?.DataType === 'date') {
      return 'date';
    }
    return 'other';
  }, [metadata?.type, metadata?.dataType, metadata?.DataType]);

  const error = field.state.meta.errors[0] as string | undefined;

  const label = useMemo(
    () => `${metadata.title || metadata.caption || fieldName}${isRequired ? ' *' : ''}`,
    [metadata.title, metadata.caption, fieldName, isRequired]
  );

  const columns = useMemo(
    () => (metadata.editor === 'selectgrid' ? parseRelatedColumns(metadata.additionalData) : []),
    [metadata.editor, metadata.additionalData]
  );

  const fieldRef = useRef(field);
  useLayoutEffect(() => {
    fieldRef.current = field;
  });

  const defaultAppliedRef = useRef(false);
  const resolvedDefaultLookupId = useMemo(
    () =>
      defaultLookupId ??
      getDetailFormJsMethodDefaultLookupId(metadata.nameJsMethod, lookupDataArray),
    [defaultLookupId, lookupDataArray, metadata.nameJsMethod]
  );

  useEffect(() => {
    if (defaultAppliedRef.current) return;
    if (!resolvedDefaultLookupId) return;
    if (hasLookupValue(fieldRef.current.state.value)) return;

    defaultAppliedRef.current = true;
    fieldRef.current.handleChange(resolvedDefaultLookupId);
  }, [resolvedDefaultLookupId]);

  const getLookupItemLabel = (item: LookupDataItem): string => {
    const id = String(item.Id);
    if (item.DisplayText) return item.DisplayText;
    if (lookupMap?.[id]) return lookupMap[id];

    const displayExpr = metadata.additionalData?.displayExpr;
    if (displayExpr && Array.isArray(item.Fields)) {
      const displayField = item.Fields.find(
        (fieldItem) => fieldItem.Name?.toLowerCase() === displayExpr.toLowerCase()
      );
      if (displayField?.StringValue) return displayField.StringValue;
    }

    return (item.Fields || []).map((f) => f.StringValue).filter(Boolean)[0] || id;
  };

  // Helper: get display label from lookupMap or lookupDataArray (fallback)
  const getDisplayLabel = (id: string) => {
    if (!hasLookupValue(id)) return '';
    if (lookupMap && lookupMap[id]) return lookupMap[id];
    if (lookupDataArray && lookupDataArray.length > 0) {
      const found = lookupDataArray.find((it) => String(it.Id) === String(id));
      if (found) return getLookupItemLabel(found);
    }
    return '-';
  };

  const selectedLookupItem = lookupDataArray?.find(
    (item) => String(item.Id) === String(field.state.value ?? '')
  );
  const selectedLookupIsInactive =
    !!selectedLookupItem && !isActiveEmployeeLookupItem(selectedLookupItem, metadata);

  const hasArrayOptions = !!(lookupDataArray && lookupDataArray.length > 0);
  const effectiveLookupOptionMap = lookupOptionMap ?? lookupMap;
  const hasMapOptions = !!(
    effectiveLookupOptionMap && Object.keys(effectiveLookupOptionMap).length > 0
  );
  const selectedLookupIsFiltered =
    hasLookupValue(field.state.value) &&
    !!metadata.defaultFilters?.length &&
    Object.prototype.hasOwnProperty.call(lookupMap ?? {}, String(field.state.value)) &&
    !lookupDataArray?.some((item) => String(item.Id) === String(field.state.value));
  const hasLookupOptions = hasArrayOptions || hasMapOptions || selectedLookupIsFiltered;
  const selectedLookupIsInvalid = selectedLookupIsInactive || selectedLookupIsFiltered;

  const isRichTextField = isRichTextFieldMetadata(metadata);
  const isFileField = isFileFieldMetadata(metadata);

  const shellProps = { label, hideCaption, captionsWidth, error };
  const readOnlyInputLabel = disabled ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <LockOutlined sx={{ fontSize: 14 }} aria-hidden="true" />
      {label}
    </span>
  ) : (
    label
  );
  const readOnlyFieldSx = disabled
    ? {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'divider',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'divider',
        },
      }
    : undefined;

  // Render selectgrid field with grid dropdown interface
  if (metadata.editor === 'selectgrid' && metadata.additionalData?.gridId) {
    const open = hasLookupOptions && Boolean(anchorEl);
    const selectValue = String(field.state.value ?? '');
    // Don't disable while still loading — only disable when neither loading nor has options
    const effectiveDisabled = disabled || (!isLoading && !hasLookupOptions);
    const labelId = `${fieldName}-label`;
    const hasValue = hasLookupValue(field.state.value);
    const showClearButton = !disabled && hasValue && !isRequired;

    const handleClearMouseDown = (e: React.MouseEvent) => {
      suppressOpenRef.current = true;
      e.stopPropagation();
      e.preventDefault();
    };

    const handleClearClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      field.handleChange('');
      setAnchorEl(null);
      requestAnimationFrame(() => {
        suppressOpenRef.current = false;
      });
    };

    return (
      <DetailFormFieldShell {...shellProps}>
        <FormControl fullWidth size="small" error={!!error} sx={readOnlyFieldSx}>
          <InputLabel id={labelId} htmlFor={fieldName} shrink>
            {readOnlyInputLabel}
          </InputLabel>
          <OutlinedInput
            id={fieldName}
            label={readOnlyInputLabel}
            notched
            value={isLoading ? 'Loading...' : hasLookupOptions ? getDisplayLabel(selectValue) : ''}
            readOnly
            aria-labelledby={labelId}
            onClick={(e) => {
              if (suppressOpenRef.current) {
                suppressOpenRef.current = false;
                return;
              }
              const target = e.target as HTMLElement;
              if (target.closest('[data-clear-btn]')) return;
              if (!effectiveDisabled) setAnchorEl(e.currentTarget);
            }}
            onKeyDown={(e) => {
              if (!effectiveDisabled && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setAnchorEl(e.currentTarget);
              }
            }}
            inputProps={{
              role: 'combobox',
              'aria-expanded': open,
              'aria-haspopup': 'dialog',
              tabIndex: effectiveDisabled ? -1 : 0,
              style: { cursor: effectiveDisabled ? 'default' : 'pointer' },
            }}
            endAdornment={
              <InputAdornment position="end" sx={{ gap: 0 }}>
                {showClearButton && (
                  <IconButton
                    size="small"
                    aria-label={`Clear ${label}`}
                    data-clear-btn="true"
                    onMouseDown={handleClearMouseDown}
                    onClick={handleClearClick}
                    sx={{ p: 0.25 }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                <ArrowDropDown
                  sx={{ color: !hasLookupOptions ? 'action.disabled' : 'action.active' }}
                />
              </InputAdornment>
            }
            sx={{
              cursor: effectiveDisabled ? 'default' : 'pointer',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: disabled ? 'divider' : undefined,
              },
              '& input': { color: selectedLookupIsInvalid ? 'error.main' : undefined },
            }}
          />
        </FormControl>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableScrollLock
          PaperProps={{
            sx: {
              minWidth: metadata.additionalData?.lookupWidth ?? 400,
              maxHeight: 450,
            },
          }}
        >
          {open && (
            <LookupGridPanel
              gridId={metadata.additionalData.gridId}
              columns={columns}
              usePaging={false}
              staticData={lookupDataArray}
              onSelect={(id) => {
                field.handleChange(id);
                setAnchorEl(null);
              }}
            />
          )}
        </Popover>
      </DetailFormFieldShell>
    );
  }

  // Fallback to simple select for selectlookup or when gridId is missing
  if (metadata.editor === 'selectgrid' || metadata.editor === 'selectlookup') {
    const labelId = `${fieldName}-label`;
    const canOpenSelect = !disabled && hasLookupOptions;
    const canClearSelect = canOpenSelect && hasLookupValue(field.state.value) && !isRequired;

    return (
      <DetailFormFieldShell {...shellProps}>
        <FormControl fullWidth size="small" error={!!error} sx={readOnlyFieldSx}>
          <InputLabel id={labelId} htmlFor={fieldName} shrink>
            {readOnlyInputLabel}
          </InputLabel>
          <Select
            id={fieldName}
            labelId={labelId}
            label={readOnlyInputLabel}
            disabled={disabled || !hasLookupOptions}
            value={field.state.value || ''}
            sx={{
              color: selectedLookupIsInvalid ? 'error.main' : undefined,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: disabled ? 'divider' : undefined,
              },
            }}
            open={canOpenSelect && selectOpen}
            onOpen={() => {
              if (canOpenSelect) setSelectOpen(true);
            }}
            onClose={() => setSelectOpen(false)}
            onChange={(e) => {
              field.handleChange(e.target.value);
              setSelectOpen(false);
            }}
            displayEmpty
            inputProps={{ 'aria-label': label, 'aria-readonly': disabled }}
            IconComponent={() => null}
            endAdornment={
              <InputAdornment position="end" sx={{ gap: 0, pointerEvents: 'none' }}>
                {canClearSelect && (
                  <IconButton
                    size="small"
                    aria-label={`Clear ${label}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      field.handleChange('');
                    }}
                    sx={{ p: 0.25, pointerEvents: 'auto' }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  tabIndex={-1}
                  disabled={!canOpenSelect}
                  aria-label={`Open ${label}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setSelectOpen(true)}
                  sx={{ p: 0.25, pointerEvents: 'auto' }}
                >
                  <ArrowDropDown
                    sx={{
                      color: !hasLookupOptions ? 'action.disabled' : 'action.active',
                    }}
                  />
                </IconButton>
              </InputAdornment>
            }
          >
            {!hasLookupOptions ? (
              <MenuItem value="">Loading...</MenuItem>
            ) : hasArrayOptions ? (
              [
                ...(selectedLookupIsFiltered
                  ? [
                      <MenuItem
                        key={`filtered-${String(field.state.value)}`}
                        value={String(field.state.value)}
                        disabled
                        sx={{ color: 'error.main' }}
                      >
                        {getDisplayLabel(String(field.state.value))}
                      </MenuItem>,
                    ]
                  : []),
                ...lookupDataArray.map((it) => (
                  <MenuItem
                    key={String(it.Id)}
                    value={String(it.Id)}
                    sx={{
                      color: isActiveEmployeeLookupItem(it, metadata) ? undefined : 'error.main',
                    }}
                  >
                    {getLookupItemLabel(it)}
                  </MenuItem>
                )),
              ]
            ) : hasMapOptions ? (
              [
                ...(selectedLookupIsFiltered
                  ? [
                      <MenuItem
                        key={`filtered-${String(field.state.value)}`}
                        value={String(field.state.value)}
                        disabled
                        sx={{ color: 'error.main' }}
                      >
                        {getDisplayLabel(String(field.state.value))}
                      </MenuItem>,
                    ]
                  : []),
                ...Object.entries(effectiveLookupOptionMap ?? {}).map(([id, displayLabel]) => (
                  <MenuItem key={id} value={id}>
                    {displayLabel}
                  </MenuItem>
                )),
              ]
            ) : selectedLookupIsFiltered ? (
              <MenuItem value={String(field.state.value)} disabled sx={{ color: 'error.main' }}>
                {getDisplayLabel(String(field.state.value))}
              </MenuItem>
            ) : null}
          </Select>
        </FormControl>
      </DetailFormFieldShell>
    );
  }

  if (metadata?.type === 'boolean' || metadata?.dataType === 'boolean') {
    return (
      <DetailFormFieldShell {...shellProps} hideCaption>
        <FormControlLabel
          control={
            <Checkbox
              checked={isBooleanValueTrue(field.state.value)}
              onChange={(e) => {
                if (!disabled) {
                  field.handleChange(toBooleanStorageValue(e.target.checked));
                }
              }}
              inputProps={{ readOnly: disabled, 'aria-readonly': disabled }}
            />
          }
          label={readOnlyInputLabel}
        />
      </DetailFormFieldShell>
    );
  }

  if (isRichTextField) {
    return (
      <DetailFormFieldShell {...shellProps} hideCaption>
        <RichTextEditor
          value={typeof field.state.value === 'string' ? field.state.value : ''}
          onChange={(content) => field.handleChange(content)}
          label={readOnlyInputLabel}
          labelId={`${fieldName}-rich-text-label`}
          readOnly={disabled}
          enableMediaUpload={
            richTextOptions?.enableMediaUpload ??
            metadata.editor?.trim().toLowerCase() === 'tinymceeditor'
          }
          uploadContextId={richTextOptions?.uploadContextId}
          maxImageWidth={Number(metadata.maxImageWidht ?? metadata.maxImageWidth) || undefined}
          maxImageHeight={Number(metadata.maxImageHeight) || undefined}
        />
      </DetailFormFieldShell>
    );
  }

  if (isFileField) {
    return (
      <DetailFormFileField
        fieldName={fieldName}
        metadata={metadata}
        field={field}
        readOnly={disabled}
        isRequired={isRequired}
        hideCaption={hideCaption}
      />
    );
  }

  // Render textarea field
  if (metadata?.editor === 'textarea') {
    return (
      <DetailFormFieldShell {...shellProps}>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={4}
          label={readOnlyInputLabel}
          InputLabelProps={{ shrink: true }}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          error={!!error}
          InputProps={{ readOnly: disabled }}
          inputProps={{ 'aria-label': label }}
          sx={readOnlyFieldSx}
        />
      </DetailFormFieldShell>
    );
  }

  // Render date / datetime field
  if (fieldType === 'date' || fieldType === 'datetime') {
    const displayValue = formatDateTimeForInput(field.state.value, fieldType) as Date | null;

    return (
      <DetailFormFieldShell {...shellProps}>
        <CustomDatePicker
          value={displayValue}
          mode={fieldType}
          label={label}
          readOnly={disabled}
          error={!!error}
          onChange={(newValue) => {
            if (!newValue) {
              field.handleChange(null);
              return;
            }
            if (fieldType === 'date') {
              field.handleChange(toBackendDateString(newValue));
              return;
            }
            field.handleChange(toBackendDateTimeString(newValue));
          }}
        />
      </DetailFormFieldShell>
    );
  }

  // Render number field
  if (metadata?.type === 'number' || metadata?.dataType === 'number') {
    return (
      <DetailFormFieldShell {...shellProps}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={readOnlyInputLabel}
          InputLabelProps={{ shrink: true }}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          error={!!error}
          InputProps={{ readOnly: disabled }}
          inputProps={{ 'aria-label': label }}
          sx={readOnlyFieldSx}
        />
      </DetailFormFieldShell>
    );
  }

  if (metadata.isUrl === true) {
    const urlValue = typeof field.state.value === 'string' ? field.state.value.trim() : '';
    let isValidUrl = false;
    if (urlValue) {
      try {
        const parsedUrl = new URL(urlValue);
        isValidUrl = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
      } catch {
        // Keep the open button disabled for invalid URL values.
      }
    }

    return (
      <DetailFormFieldShell {...shellProps}>
        <TextField
          fullWidth
          size="small"
          label={label}
          InputLabelProps={{ shrink: true }}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          error={!!error}
          disabled={disabled}
          inputProps={{ 'aria-label': label }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={`Open ${label}`}
                  disabled={!isValidUrl}
                  onClick={() => window.open(urlValue, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DetailFormFieldShell>
    );
  }

  // Render text field (default)
  return (
    <DetailFormFieldShell {...shellProps}>
      <TextField
        fullWidth
        size="small"
        label={readOnlyInputLabel}
        InputLabelProps={{ shrink: true }}
        value={field.state.value ?? ''}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        error={!!error}
        InputProps={{ readOnly: disabled }}
        inputProps={{ 'aria-label': label }}
        sx={readOnlyFieldSx}
      />
    </DetailFormFieldShell>
  );
};

DetailFormField.displayName = 'DetailFormField';
